import React, { useState, useEffect } from 'react';
import { Form } from '@ui/forms/form';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import { useForm, Controller } from 'react-hook-form';
import { FormSelect, Option } from '@ui/forms/select/select';

import { MoreVertIcon } from '@ui/icons/material/MoreVert';
import { EditIcon } from '@ui/icons/material/Edit';
import { DeleteIcon } from '@ui/icons/material/Delete';
import { CloseIcon } from '@ui/icons/material/Close';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';
import { useAuth } from '@common/auth/use-auth';
import { PrimitiveValue } from '@common/ui/forms/listbox/types';

interface CategoryOption {
  value: string;
  label: string;
}

const categoryOptions: CategoryOption[] = [
  { value: 'musica', label: 'Música' },
  { value: 'danca', label: 'Dança' },
  { value: 'entretenimento', label: 'Entretenimento' },
  { value: 'esportes', label: 'Esportes' },
];

interface VideoFormValues {
  title: string;
  description: string;
  category: string;
  location: string;
}

interface VideoActionsProps {
  clip: {
    id: number;
    user_id: number;
    title: string;
    description: string;
    category: string;
    location: string;
  };
  onUpdate: () => void;
}

const DeleteConfirmationDrawer = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    videoTitle 
  }: { 
    isOpen: boolean; 
    onClose: () => void; 
    onConfirm: () => void;
    videoTitle: string;
  }) => {
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" 
          onClick={onClose}
        />
        <div className="absolute inset-x-0 bottom-0 max-h-screen transform transition-transform duration-300 ease-in-out">
          <div className="relative rounded-t-xl bg dark:bg-gray-900 shadow-xl px-6 pt-6 pb-8" 
               style={{ maxWidth: '70vh', margin: 'auto', paddingBottom: '2.375rem', paddingTop: '1.375rem' }}>
            
            <div className="mb-6 text-center">
              <DeleteIcon className="w-7 h-7 text-red-500 mx-auto mb-4" style={{ width: '1.75rem', height: '1.75rem' }} />
              <h2 className="text-xl font-semibold mb-2">Confirmar Exclusão</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Tem certeza que deseja deletar o vídeo:
                <br />
                <span className="font-medium">{videoTitle}</span>?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Esta ação não pode ser desfeita.
              </p>
            </div>
            
            <div className="flex gap-3 justify-center" style={{ gap: '2.188rem' }}>
              <Button
                variant="outline"
                onClick={onClose}
                className="w-32 px-8" // padding ajustado para 3.125rem dos dois lados
                style={{ paddingLeft: '3.125rem', paddingRight: '3.125rem' }}
              >
                Cancelar
              </Button>
              <Button
                variant="flat"
                color="danger"
                onClick={onConfirm}
                className="w-32 px-8" // padding ajustado para 3.125rem dos dois lados
                style={{ paddingLeft: '3.125rem', paddingRight: '3.125rem' }}
              >
                Deletar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  

const VideoActionsMenu = ({ clip, onUpdate }: VideoActionsProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { user } = useAuth();
  
  const form = useForm<VideoFormValues>({
    defaultValues: {
      title: clip.title || '',
      description: clip.description || '',
      category: clip.category || '',
      location: clip.location || '',
    },
  });

  useEffect(() => {
    form.reset({
      title: clip.title || '',
      description: clip.description || '',
      category: clip.category || '',
      location: clip.location || '',
    });
  }, [clip, form]);

  const isOwner = user?.id === clip.user_id;

  if (!isOwner) return null;

  const handleEdit = async (values: VideoFormValues) => {
    try {
      const formData = {
        action: 'edit',
        id: clip.id,
        user_id: user?.id,
        title: values.title,
        description: values.description,
        category: values.category,
        location: values.location,
      };

      const response = await fetch('/profile-user-clips.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const text = await response.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response:', text);
        throw new Error('Invalid JSON response from server');
      }

      if (data.status === 'success') {
        toast.positive(message('✏️ Vídeo atualizado com sucesso!'));
        setIsEditDrawerOpen(false);
        onUpdate();
      } else {
        throw new Error(data.message || 'Failed to update video');
      }
    } catch (error) {
      console.error('Edit error:', error);
      toast.danger(message('⚠️ Erro ao atualizar o vídeo'));
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch('/profile-user-clips.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'delete',
          id: clip.id,
          user_id: user?.id,
        }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        toast.positive(message('🗑️ Vídeo deletado com sucesso!'));
        setIsDeleteConfirmOpen(false);
        setIsMenuOpen(false);
        onUpdate();
      } else {
        throw new Error(data.message || 'Failed to delete video');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.danger(message('⚠️ Erro ao deletar o vídeo'));
    }
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{ background: '#9d9d9d5e' }}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <MoreVertIcon className="w-[25px] h-[25px]" />
        </button>

        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-[6rem] rounded-md shadow-lg bg dark:bg-gray-800 ring-1 ring-black ring-opacity-5">
            <div className="py-1">
              <button
                onClick={() => {
                  setIsEditDrawerOpen(true);
                  setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <EditIcon className="w-[25px] h-[25px] mr-2" />
                Editar
              </button>
              <button
                onClick={() => {
                  setIsDeleteConfirmOpen(true);
                  setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <DeleteIcon className="w-[25px] h-[25px] mr-2" />
                Deletar
              </button>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmationDrawer
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        videoTitle={clip.title}
      />

      {isEditDrawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsEditDrawerOpen(false)} />
          <div className="absolute inset-y-0 right-0 max-w-full flex">
            <div className="relative w-screen max-w-md">
              <div className="h-full bg dark:bg-gray-900 shadow-xl flex flex-col">
                <div className="px-4 py-6 sm:px-6">
                  <div className="flex items-start justify-between">
                    <h2 className="text-lg font-medium">Editar</h2>
                    <button
                      onClick={() => setIsEditDrawerOpen(false)}
                      className="rounded-md text-gray-400 hover:text-gray-500"
                    >
                      <CloseIcon className="w-[25px] h-[25px]" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 px-4 sm:px-6 overflow-auto">
                  <Form form={form} onSubmit={handleEdit}>
                    <div className="space-y-6">
                      <TextField
                        label="Título"
                        {...form.register('title')}
                        className="w-full"
                      />
                      <TextField
                        label="Descrição"
                        {...form.register('description')}
                        className="w-full"
                        inputElementType="textarea"
                        rows={4}
                      />
                      <Controller
                        name="category"
                        control={form.control}
                        defaultValue={clip.category}
                        render={({ field }) => (
                          <FormSelect
                            name={field.name}
                            label="Categoria"
                            className="w-full"
                            selectionMode="single"
                            selectedValue={field.value}
                            onSelectionChange={(value: PrimitiveValue) => field.onChange(value as string)}
                          >
                            {categoryOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </FormSelect>
                        )}
                      />
                      <TextField
                        label="Localização"
                        {...form.register('location')}
                        className="w-full"
                      />
                      <div className="flex justify-end gap-3">
                        <Button
                          variant="outline"
                          onClick={() => setIsEditDrawerOpen(false)}
                        >
                          Cancelar
                        </Button>
                        <Button type="submit" variant="flat" color="primary">
                          Salvar Alterações
                        </Button>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoActionsMenu;